import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import axiosRetry from 'axios-retry'
import { v4 as uuidv4 } from 'uuid'

import { setToast } from '@/entities/toast'
import { OLMS_API_HOST, OLMS_API_KEY } from '@/shared/config/consts'
import { EntityId } from '@reduxjs/toolkit'

import { store } from '../../store'

const OLMSClient = axios.create({
  baseURL: OLMS_API_HOST,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Api-Key': OLMS_API_KEY,
  },
})

axiosRetry(OLMSClient, {
  retries: 3,
  retryDelay: (retryCount) => {
    return retryCount * 1000
  },
  // Optionally you can define conditions to determine whether to retry or not
  retryCondition: (error) => {
    return error.response === undefined
  },
})

OLMSClient.interceptors.request.use(config => {
  const state = store.getState()
  const { token } = state.session
  const { activeGroupId } = state.userInterface

  if (token) {
    config.headers.set('X-Auth-Token', token)
  }

  if (activeGroupId) {
    config.headers.set('X-Group', activeGroupId.toString())
  }

  if (!config.headers['X-Request-ID']) {
    config.headers['X-Request-ID'] = uuidv4()
  }

  return config
}, error => {
  return Promise.reject(error)
})

class AxiosApiService {
  private instance: AxiosInstance

  constructor() {
    this.instance = OLMSClient
  }

  async get<TResult>(endpoint: string, params?: object): Promise<TResult | null> {
    try {
      const response: AxiosResponse<TResult> = await this.instance.get(endpoint, { params })

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw error
      }
      else if (axios.isAxiosError(error)) {
        store.dispatch(setToast({
          open: true,
          severity: 'error',
          title: `Error ${error.name} ${error.status}`,
          content: `An error occurred while fetching data from the server: ${error.message}.\nMethod: GET \nEndpoint: ${endpoint}`,
        }))
      } else {
        store.dispatch(setToast({
          open: true,
          severity: 'error',
          title: 'Error',
          content: 'An error occurred while fetching data from the server.\nMethod: GET \nEndpoint: ${endpoint}',
        }))
      }

      throw error
    }

    return null
  }

  /*
    Sends a POST request to the server with the given data.
  */
  async post<TResult>(endpoint: string, data?: object | EntityId, config?: AxiosRequestConfig): Promise<TResult | null> {
    try {
      const response: AxiosResponse<TResult> = await this.instance.post(endpoint, data, config)

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw error
      }
      else if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message ? `<strong>Message:</strong> ${error.response?.data?.message}` :
          `An error returned from the server: ${error.message}.`

        store.dispatch(setToast({
          open: true,
          severity: 'error',
          title: `Error ${error.code}`,
          content: `${errorMessage}<br />
            <strong>Method:</strong> POST<br />
            <strong>Endpoint:</strong> ${endpoint}
          `,
        }))
      } else {
        store.dispatch(setToast({
          open: true,
          severity: 'error',
          title: 'Error',
          content: `An error returned from the server.<br />
          <strong>Method:</strong> POST <br />
          <strong>Endpoint:</strong> ${endpoint}`,
        }))
      }

      throw error
    }
  }

  /*
    Uploads a file to the server using a POST request with a FormData object.
  */
  async upload<TResult>(endpoint: string, formData: FormData, onProgress?: (progress: number) => void): Promise<TResult | null> {
    try {
      const response = await this.instance.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1))

            onProgress(percentCompleted)
          }
        },
      })

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message
          ? `<strong>Message:</strong> ${error.response?.data?.message}`
          : `An error occurred during file upload: ${error.message}.`

        store.dispatch(setToast({
          open: true,
          severity: 'error',
          title: `Error ${error.code}`,
          content: `${errorMessage}<br />
            <strong>Method:</strong> POST<br />
            <strong>Endpoint:</strong> ${endpoint}
          `,
        }))
      } else {
        store.dispatch(setToast({
          open: true,
          severity: 'error',
          title: 'Error',
          content: `An error occurred during file upload.<br />
          <strong>Method:</strong> POST <br />
          <strong>Endpoint:</strong> ${endpoint}`,
        }))
      }

      throw error
    }
  }
}

export const OLMSApiService = new AxiosApiService()
